/* eslint-disable prettier/prettier */ import type { RootState } from 'core/store';
import type { Checkout } from 'core/domains';
import type { SelectorReturnFn } from 'core/use-cases/utilities';
import { money, formatMoney } from 'core/utilities';
import { selectors as userSelectors } from 'core/use-cases/user';
import { useSelector } from 'core/use-cases/utilities';

/**
 * Selectors return info of checkout context
 *
 */
export function useCheckoutSelectors() {
  const data = useSelector<Checkout>(({ checkout }) => checkout);

  return data;
}

/**
 * Selectors return payment option
 *
 */
export function usePaymentOptionSelectors() {
  const data = useSelector<Checkout['paymentOption']>(({ checkout }) => checkout.paymentOption);

  return data;
}

/**
 * Selectors the value of a property through its name
 *
 */
export function useCheckoutPropertyByKeySelector<T extends keyof Checkout>(key: T) {
  const value = useSelector<Checkout[T]>(({ checkout }) => checkout[key]);

  return value;
}

export function usePaymentDetailSelector(): SelectorReturnFn<Checkout['paymentDetail']['data']> {
  const { data, status, updatedAt = 0 } = useSelector((state) => state.checkout.paymentDetail);

  return {
    data,
    status,
    updatedAt,
  };
}

export function usePaymentMethodsSelector(): SelectorReturnFn<Checkout['paymentMethods']['data']> {
  const { data, status, updatedAt = 0 } = useSelector((state) => state.checkout.paymentMethods);

  return {
    data,
    status,
    updatedAt,
  };
}

type SummaryData = {
  cart: Pick<RootState['cart']['data'], 'items'> & Pick<RootState['cart'], 'isRunning'>;
  fullAddress: RootState['user']['data']['fullAddress'];
  isNotificationVisible: RootState['checkout']['isNotificationVisible'];
  paymentOption: RootState['checkout']['paymentOption'];
  rewards: RootState['checkout']['rewards']['data'];
  shippingModel: {
    businessModelCode: RootState['cart']['current']['shippingModel']['businessModelCode'];
    distributionCenterAddress: RootState['cart']['current']['distributionCenterAddress'];
  };
  values: Pick<RootState['cart']['data'], 'shipping' | 'total' | 'subTotal'> &
    Pick<
      RootState['checkout']['paymentMethods']['data']['bankSlip']['paymentInfo'][number],
      'administrateTax' | 'name' | 'paymentPlanType'
    > & { summaryTotal: string };
};

export function useSummarySelector(): SelectorReturnFn<SummaryData> {
  const { data: cartData, isRunning } = useSelector(({ cart }) => cart);
  const {
    data: { fullAddress, shippingModel: shippingModelDefault },
  } = userSelectors.useUserSelector();
  const {
    isNotificationVisible,
    paymentDetail: { status, updatedAt = 0 },
    paymentOption,
    rewards,
  } = useSelector(({ checkout }) => checkout);
  const {
    current: { distributionCenterAddress, shippingModel },
  } = useSelector(({ cart }) => cart);

  const PaymentTotal = formatMoney(money(cartData.totalNum).value);

  const SummaryTotal = formatMoney(
    money(cartData.totalNum).add(paymentOption.administrateTaxNum!).add(cartData.shippingNum).value,
  );

  const data = {
    fullAddress,
    cart: {
      items: cartData.items,
      isRunning,
    },
    isNotificationVisible,
    paymentOption,
    shippingModel: {
      businessModelCode: shippingModel?.businessModelCode || shippingModelDefault?.default?.code,
      distributionCenterAddress:
        distributionCenterAddress || shippingModelDefault?.default?.delivery?.address,
    },
    rewards: rewards?.data,
    values: {
      administrateTax: paymentOption?.administrateTax,
      cashback: cartData.cashback,
      cashbackNum: cartData.cashbackNum,
      paymentPlanType: paymentOption.paymentPlanType,
      shipping: cartData.shipping,
      subTotal: cartData.subTotal,
      summaryTotal: SummaryTotal,
      total: PaymentTotal,
      totalDiscount: cartData.totalDiscount,
      totalDiscountNum: cartData.totalDiscountNum,
    },
  };

  return {
    data,
    status,
    updatedAt,
  };
}
